import {
    DialogContent, Pagination, Paper, Typography
} from '@mui/material';
import clsx from 'clsx';
import { formatISO, parseISO, startOfWeek } from 'date-fns';
import {
    Fragment, ReactNode, memo, useEffect, useMemo, useRef, useState
} from 'react';

import {
    Dialog, DialogActions, DialogProps, RoutedDialog, RoutedDialogProps
} from '..';
import { PaginatedResponse } from '../../api/common';
import { Either } from '../../utils';

import styles from './GroupedTimelineDialog.module.scss';
import DayDivider from './components/DayDivider';
import { groupByDate } from './utils/groupByDate';
import { BreakpointNodesRef, useScrollBreakpoints } from './utils/useScrollBreakpoints';


type GroupedTimelineDialogPropsBase = (DialogProps & { isRouted?: false }) | (RoutedDialogProps & { isRouted: true });

type GroupedTimelineDialogProps<TItem extends {}> = GroupedTimelineDialogPropsBase & {
    dateKey: keyof TItem;
    filterForm: ReactNode;
    renderItem: (item: TItem, index: number) => ReactNode;
} & Either<{
    items: TItem[] | undefined;
}, {
    paginatedItems: PaginatedResponse<TItem> | undefined;
    pageLoading?: boolean;
    onPageChange: (page: number) => void;
}>;

export function GroupedTimelineDialog<TItem extends {}>({
    items: itemsProp,
    paginatedItems,
    dateKey, filterForm,
    renderItem,
    onPageChange,
    pageLoading,
    isRouted,
    ...dialogProps
}: GroupedTimelineDialogProps<TItem>) {
    const items = itemsProp || paginatedItems?.data;
    const headerNodesRef: BreakpointNodesRef = useRef([]);
    const containerRef = useRef<HTMLElement | null>(null);

    const {
        currentNode, handleScroll, scrollDirection, isScrolledDown
    } = useScrollBreakpoints(headerNodesRef);

    const sortedGroups = useMemo(() => Object.entries(
        groupByDate(items, dateKey)
    ).sort(([ dateStrA ], [ dateStrB ]) => (
        parseISO(dateStrB).getTime() - parseISO(dateStrA).getTime()
    )), [ items, dateKey ]);

    const defaultStuckNodeDateStr = sortedGroups[0]?.[0] || formatISO(new Date(), { representation: 'date' });
    const [ stuckHeaderDate, setStuckHeaderDate ] = useState<Date>(parseISO(defaultStuckNodeDateStr));
    const [ showScrollbarPlug, setShowScrollbarPlug ] = useState(false);

    useEffect(() => {
        const dateStr = currentNode?.dataset.dayHeaderDate || defaultStuckNodeDateStr;

        if (dateStr) {
            const date = parseISO(dateStr);

            if (date.getTime() !== stuckHeaderDate?.getTime()) {
                setStuckHeaderDate(parseISO(dateStr));
            }
        }
    }, [
        currentNode, defaultStuckNodeDateStr, stuckHeaderDate
    ]);

    function shouldRenderWeek(index: number) {
        const itemDate = parseISO(sortedGroups[index][0]);
        const prevItemDate = parseISO(sortedGroups[index - 1]?.[0]);

        return startOfWeek(itemDate).getTime() !== (prevItemDate && startOfWeek(prevItemDate).getTime());
    }

    const children = (
        <>
            <DialogContent
                className={clsx(styles.filters, {
                    [styles.scrolledDown]: isScrolledDown
                })}
            >
                <Typography
                    variant="body2"
                    color="textSecondary"
                >
                    Filters
                </Typography>

                {filterForm}

                <DayDivider
                    date={stuckHeaderDate}
                    scrollDirection={scrollDirection}
                    showStartOfWeek
                    showScrollbarPlug={showScrollbarPlug}
                />
            </DialogContent>

            <DialogContent
                className={styles.mainContent}
                onScroll={handleScroll}
                ref={(node: HTMLElement | null) => {
                    if (node !== containerRef.current) {
                        containerRef.current = node;

                        if (node) {
                            setShowScrollbarPlug(node.offsetWidth !== node.scrollWidth);
                        }
                    }
                }}
            >
                {!sortedGroups.length ? (
                    <Paper
                        variant="outlined"
                        className={styles.noResults}
                    >
                        <Typography
                            fontWeight={500}
                            color="textSecondary"
                        >
                            No results found
                        </Typography>

                        <Typography
                            variant="caption"
                            color="textSecondary"
                        >
                            Try adjusting the filters to find other results
                        </Typography>
                    </Paper>
                ) : sortedGroups.map(([ dateStr, groupItems ], index) => (
                    <Fragment key={dateStr}>
                        {index !== 0 && (
                            <DayDivider
                                date={parseISO(dateStr)}
                                showStartOfWeek={shouldRenderWeek(index)}
                                headerRef={(node) => headerNodesRef.current[index] = node}
                                currentNode={currentNode}
                            />
                        )}

                        <MemoedGroupItems
                            items={groupItems}
                            renderItem={renderItem}
                        />
                    </Fragment>
                ))}
            </DialogContent>

            {paginatedItems && (
                <DialogActions
                    className={styles.dialogActions}
                    loading={pageLoading}
                >
                    <Pagination
                        count={paginatedItems.totalPages}
                        page={paginatedItems.pageNumber}
                        onChange={(_, page) => onPageChange?.(page)}
                        color="primary"
                    />
                </DialogActions>
            )}
        </>
    );

    return isRouted ? (
        <RoutedDialog
            {...dialogProps as RoutedDialogProps}
            loading={!items}
            children={children}
        />
    ) : (
        <Dialog
            {...dialogProps as DialogProps}
            loading={!items}
            children={children}
        />
    );
}

interface GroupItemsProps<TItem extends {}> extends Pick<GroupedTimelineDialogProps<TItem>, 'renderItem'> {
    items: TItem[];
}

function GroupItems<TItem extends {}>({ items, renderItem }: GroupItemsProps<TItem>) {
    return (
        <div>
            {items.map(renderItem)}
        </div>
    );
}
const typedMemo: <T>(c: T) => T = memo;

const MemoedGroupItems = typedMemo(GroupItems);
